import React, { useState } from "react";
import { FormattedMessage } from "react-intl";
import Clue from "../Clue/Clue";
import { intlShape } from "../../shapes";
import {
  withBrowser,
  withRules,
  rulesShape,
  apiShape,
  withApi,
  backendShape,
  withBackend,
  withNotifier,
  notifierShape,
  browserShape,
} from "../../context";
import { withIntl } from "../../wrappers";
import { htmlLinebreak, htmlLink, isNothing } from "../../utils";
import CONFIGURATION from "../../configuration";
import { useSubscriptionQuery, useUpgradeTarget } from "../../hooks";

type OwnProps = {
  browser: browserShape;
  intl: intlShape;
  backend: backendShape;
  rules?: rulesShape;
  notifier: notifierShape;
  api: apiShape;
  formValues?: {
    manufacturer?: {
      name?: string;
      tenantPublic?: boolean;
    };
    product?: {
      name?: string;
      code?: string;
    };
  };
  setSubscriptionValid?: $TSFixMeFunction;
  initialValues?: {
    product?: {
      name?: string;
      code?: string;
    };
  };
  quotaExceeded?: boolean;
};

const defaultProps: Partial<OwnProps> = {
  initialValues: undefined,
  quotaExceeded: false,
};

type Props = OwnProps & typeof defaultProps;

export function ConnectedAssetSubscriptionClue({
  api,
  backend,
  formValues,
  intl,
  browser,
  rules,
  setSubscriptionValid,
  notifier,
  initialValues,
  quotaExceeded,
}: Props) {
  const { data: subscription } = useSubscriptionQuery();
  const { target: upgradeTarget, isReady: upgradeReady } = useUpgradeTarget();

  const [status, setStatus] = useState();
  const isOneSubscription =
    subscription?.client_application?.name === "Netilion";

  const setStatusForConnectedAsset = async () => {
    // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
    const isConnectedAsset = rules
      .asset({
        manufacturerName: formValues?.manufacturer?.name,
        productCode: formValues?.product?.code,
        productTenantPublic: formValues?.manufacturer?.tenantPublic,
      })
      .get("connectedAsset");

    if (!(formValues && isConnectedAsset)) {
      return null;
    }
    if (
      // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
      !rules.application().get("hasConnectedAssetAddon") &&
      !isOneSubscription
    ) {
      return "connected_asset_no_valid_application";
    }
    const addonConnectResponse = await backend.get(
      "/subscription/addon/connected",
    );
    if (isNothing(addonConnectResponse?.addon)) {
      return "connected_asset_no_addon";
    }
    const quantityOfAddonsBought = addonConnectResponse.addon.quantity;
    const assignedToAddonAssetCount =
      addonConnectResponse.addon.assigned_assets_count;
    if (assignedToAddonAssetCount >= quantityOfAddonsBought) {
      return "connected_asset_not_enough_addons";
    }
    return null;
  };

  const getStatus = (allStatus: $TSFixMe) =>
    allStatus.filter((s: $TSFixMe) => s).join("_");

  const setStatusForFermentationAddOn = async (tmpStatus: $TSFixMe) => {
    // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
    const requiresFermentationAddOn = rules
      .asset({
        manufacturerName: formValues?.manufacturer?.name,
        productCode: formValues?.product?.code,
        productTenantPublic: formValues?.manufacturer?.tenantPublic,
      })
      .get("requiresFermentationAddOn");

    if (!(formValues && requiresFermentationAddOn)) {
      return tmpStatus;
    }
    if (
      tmpStatus === "connected_asset_no_valid_application" &&
      !isOneSubscription
    ) {
      // if there is no connected addon for the current app, there are also no fermentation addon.
      return "fermentation_no_valid_application";
    }
    const addonFermentationResponse = await backend.get(
      "/subscription/addon/fermentation",
    );
    if (isNothing(addonFermentationResponse?.addon)) {
      return getStatus([tmpStatus, "fermentation_no_addon"]);
    }
    const quantityOfAddonsBought = addonFermentationResponse.addon.quantity;
    const assignedToAddonAssetCount =
      addonFermentationResponse.addon.assigned_assets_count;
    if (assignedToAddonAssetCount >= quantityOfAddonsBought) {
      return getStatus([tmpStatus, "fermentation_not_enough_addons"]);
    }
    return tmpStatus;
  };

  const updateState = async () => {
    let tmpStatus;

    if (
      isNothing(initialValues) ||
      (initialValues as $TSFixMe)?.product?.code !== formValues?.product?.code
    ) {
      tmpStatus = await setStatusForConnectedAsset();

      tmpStatus = await setStatusForFermentationAddOn(tmpStatus);

      if (formValues && quotaExceeded) {
        tmpStatus = "connected_asset_not_enough_addons";
      }
    }

    if (tmpStatus) {
      // @ts-expect-error TS(2722) FIXME: Cannot invoke an object which is possibly 'undefin... Remove this comment to see the full error message
      setSubscriptionValid(false);
      setStatus(tmpStatus);
    } else if (formValues?.manufacturer?.name && formValues?.product?.code) {
      // @ts-expect-error TS(2722) FIXME: Cannot invoke an object which is possibly 'undefin... Remove this comment to see the full error message
      setSubscriptionValid(true);
      setStatus(undefined);
    } else {
      // @ts-expect-error TS(2722) FIXME: Cannot invoke an object which is possibly 'undefin... Remove this comment to see the full error message
      setSubscriptionValid(false);
      setStatus(undefined);
    }
  };

  React.useEffect(() => {
    updateState().catch((error) => {
      notifier.showError(api.translateError(error));
      // @ts-expect-error TS(2722) FIXME: Cannot invoke an object which is possibly 'undefin... Remove this comment to see the full error message
      setSubscriptionValid(true);
      setStatus(undefined);
    });
  }, [formValues?.manufacturer, formValues?.product, quotaExceeded]);

  const renderClue = () => {
    if (!(status && formValues?.product?.name)) {
      return null;
    }

    if ((status as $TSFixMe).includes("no_valid_application")) {
      return (
        <Clue
          id="no-valid-application-notification"
          title={intl.formatMessage(
            { id: "connected_asset_subscription_clue.title" },
            { productName: formValues?.product?.name },
          )}
          details={
            <FormattedMessage
              id={
                (status as $TSFixMe).includes("fermentation")
                  ? "connected_asset_subscription_clue.fermentation_no_valid_application.notification"
                  : "connected_asset_subscription_clue.connected_asset_no_valid_application.notification"
              }
              values={{
                productName: formValues?.product?.name,
                // @ts-expect-error TS(2322) FIXME: Type '(...chunks: any[]) => Element' is not assign... Remove this comment to see the full error message
                a: htmlLink({ href: CONFIGURATION.HELP_URL, target: "_blank" }),
                // @ts-expect-error TS(2322) FIXME: Type '() => Element' is not assignable to type 'Re... Remove this comment to see the full error message
                br: htmlLinebreak(),
              }}
            />
          }
          // @ts-expect-error TS(2322) FIXME: Type '{ id: string; title: string; details: Elemen... Remove this comment to see the full error message
          className="notification"
          highlighted
        />
      );
    }
    return (
      <Clue
        id="upgrade-notification"
        title={intl.formatMessage(
          { id: "connected_asset_subscription_clue.title" },
          { productName: formValues?.product?.name },
        )}
        details={intl.formatMessage(
          {
            id: (status as $TSFixMe).includes("no_addon")
              ? "connected_asset_subscription_clue.no_valid_subscription_details"
              : "connected_asset_subscription_clue.upgrade_remove_details",
          },
          { productName: formValues?.product?.name, condition: status },
        )}
        // @ts-expect-error TS(2322) FIXME: Type '{ id: string; title: string; details: string... Remove this comment to see the full error message
        className="notification"
        {...(upgradeReady && upgradeTarget
          ? {
              action: intl.formatMessage({
                id: "connected_asset_subscription_clue.upgrade_button",
              }),
              onClick: () => {
                browser.navigateTo(upgradeTarget);
              },
            }
          : {})}
        highlighted
      />
    );
  };

  return renderClue();
}

ConnectedAssetSubscriptionClue.defaultProps = defaultProps;

export default withBackend(
  withNotifier(
    // @ts-expect-error TS(2345) FIXME: Argument of type '{ (props: Readonly<Omit<Omit<Own... Remove this comment to see the full error message
    withApi(withRules(withBrowser(withIntl(ConnectedAssetSubscriptionClue)))),
  ),
);
